<!-- eslint-disable vue/no-multiple-template-root -->
<template>
  <div ref="outerElement">
    <div v-if="title" class="sf-pageheader sf-below-4">
      <div class="sf-grid">
        <div class="sf-row sf-row-center">
          <div class="sf-col sf-xs-12 sf-sm-5">
            <div
              class="
                sf-level sf-level-between sf-level-m sf-level-ignore-mobile
              "
            >
              <div class="sf-level-item">
                <span class="sf-pageheader-title">{{ title }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="sf-below-4">
      <div class="sf-grid">
        <div class="sf-row sf-row-center sf-below-3">
          <div class="sf-col sf-xs-12 sf-sm-5">
            <div class="sf-layer-white sf-layer-border sf-pad-3">
              <SfBannerPage
                v-if="countMessage"
                class="sf-below-3"
                :variant="bannerVariants.warning"
                is-full-width
              >
                {{ countMessage }}
              </SfBannerPage>
              <div class="tag-name-input">
                <div class="sf-row sf-below-3 string required tag_name">
                  <div class="sf-col">
                    <SfInputExtended
                      id="tag_name"
                      v-model="name"
                      autocomplete="off"
                      name="tag[name]"
                      data-testid="remark-tag-form-input"
                      :error-text="errors"
                      :label="
                        I18n.t('activerecord.attributes.adjustment_tag.name')
                      "
                      :limit="MAX_TAG_LENGTH"
                      :limit-translation="'activerecord.errors.models.tag.attributes.name.too_long'"
                      :no-clip="true"
                      :required="true"
                    />
                  </div>
                </div>
              </div>
              <div class="sf-level sf-level-right">
                <input
                  type="submit"
                  name="commit"
                  data-testid="remark-tag-form-submit"
                  :value="submitLabel"
                  class="sf-btn sf-btn-success"
                  :class="{ 'sf-disabled': disableNameField }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'

import { SfInputExtended } from '@app/shared/vue/components/sf-input'
import SfBannerPage from '@app/shared/vue/components/sf-banner/sf-banner-page.vue'
import { bannerVariants } from '@app/shared/types/banner'

const MAX_TAG_LENGTH = 20

interface Props {
  countMessage?: string
  errors?: string[]

  initialValue?: string
  submitLabel?: string
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  countMessage: '',
  errors: () => [],
  initialValue: '',
  submitLabel: '',
  title: '',
})

const outerElement = ref<HTMLElement | null>(null)
const name = ref(props.initialValue)
const submitInProgress = ref(false)
const formRef = ref<HTMLFormElement | null>(null)

const remainingCharacters = computed(() => {
  return MAX_TAG_LENGTH - name.value.length
})

const invalidName = computed(() => {
  return remainingCharacters.value < 0
})

const disableNameField = computed(() => {
  return (
    invalidName.value ||
    props.initialValue === name.value ||
    submitInProgress.value
  )
})

function onSubmit(e: SubmitEvent) {
  e.preventDefault()
  e.stopPropagation()

  if (props.initialValue !== '' && props.initialValue !== name.value) {
    const i18nData = {
      old_tag: props.initialValue,
      new_tag: name.value,
    }

    window.sfConfirm({
      question: I18n.t('js.remark_tags.confirm_edit_question', i18nData),
      message: I18n.t('js.remark_tags.confirm_edit_message', i18nData),
      onConfirm: submitForm,
    })
  } else {
    submitForm()
  }
}

onMounted(() => {
  if (outerElement.value) {
    const form = outerElement.value.closest('form') as HTMLFormElement
    form?.addEventListener('submit', onSubmit)
    formRef.value = form
  }
})

onBeforeUnmount(() => {
  formRef.value?.removeEventListener('submit', onSubmit)
})

function submitForm() {
  submitInProgress.value = true // no need to clear this flag, submitting the form navigates away
  formRef.value?.submit()
}

const I18n = window.I18n
</script>
